import { default as React, useEffect, useRef } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { MiniLoader } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/tokens';
import styled from '@emotion/styled';

import { UBLProvider } from '@common/log-manager';
import { Gray_Light_White } from '@common/styles';
import { FaceButton, FaceButtonSize } from '@common/toolbox';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { PdpConfigProvider } from '@domains/pdp-common';

import { Container } from '../containers/Container';
import { CommonView } from '../containers/options-view/CommonView';
import { OptionProvider } from '../context/OptionProvider';
import { useOptionsControl } from '../hooks/useOptionsControl';
import { useOptions } from '../queries/useOptions';
import { ItemCardType } from '../types/option-type';

interface Props {
  id?: string;
  isAdditional?: boolean;
  productId?: string;
  maximumLimit?: number;
  onEdit?: (items: ItemCardType[]) => void;
  bogo_required_quantity?: number;
}

const ExchangeOptionView = ({ isAdditional, onEdit, bogo_required_quantity }: Props) => {
  const { options, isFetched } = useOptions();
  const { initialize, removeItem, isNoOption, setIsSingleSelectionAllowed, itemCards } = useOptionsControl();
  const visibleOptionSelect = isAdditional ? !isAdditional : !isNoOption;
  const disabledEditButton = itemCards.length === 0;

  function handleEditClick() {
    onEdit?.(itemCards);
  }

  useEffect(() => {
    setIsSingleSelectionAllowed(true);
  }, []);
  /**
   * 1개 이상 아이템이 선택되면, 상품변경 정책으로 인해 기존 아이템을 제거한다.
   */
  useEffect(() => {
    if (itemCards.length > 1) {
      const { item } = itemCards[0];
      removeItem(item);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemCards]);

  /**
   * 교환처리하는 시점에 bogo 행사가 종료될 경우, 전시 API에서 bogo 처리를 할 수 없음
   * 전달받은 bogo_required_quantity를 이용하여 bogo 정보를 초기화한다.
   */
  useEffect(() => {
    if (isFetched) {
      const bogoInfo = {
        discount_amount: null,
        discount_rate_bp: null,
        discount_type: null,
        required_quantity: bogo_required_quantity ?? null,
        badge: null,
      };

      initialize({
        options_data: options.catalog_product,
        shipping_fee: options.catalog_product.shipping_fee,
        bogo_info: bogoInfo,
      });
    }
  }, [isFetched, bogo_required_quantity]);

  if (!options) {
    return null;
  }

  return (
    <Container>
      <Wrapper>
        {isFetched ? (
          <>
            <CommonView
              isLoggedIn={false}
              onEdit={handleEditClick}
              isEditable
              visibleAdditional={isAdditional}
              visibleOptionSelect={visibleOptionSelect}
              disabledShipping
              disabledPrice
              hiddenCounter
              disabledConfirm
              disabledAutoSelect
            />
            {!disabledEditButton && (
              <ButtonWrapper>
                <EditButton
                  className='btn'
                  size_type={FaceButtonSize.S15_H48}
                  style={{ backgroundColor: vars.v3Color.black }}
                  borderColor={vars.v3Color.black}
                  textColor={vars.v3Color.white}
                  onClick={handleEditClick}
                >
                  선택 완료
                </EditButton>
              </ButtonWrapper>
            )}
          </>
        ) : (
          <Loader />
        )}
      </Wrapper>
    </Container>
  );
};

export const ExchangeOptionScreen = (props: Props) => {
  const queryClientRef = useRef<QueryClient>(new QueryClient());

  return (
    <PdpConfigProvider
      isZigzagApp
      isProductPreview
      catalogProductId={props.id}
      path={{
        auth: {
          login: '/login',
        },
        review: {
          list: () => '/review',
        },
      }}
    >
      {/* TODO: UBL navigation 챙길 것 */}
      <UBLProvider navigation={'zpay_browser_cart_option'}>
        <OptionProvider entry_source_type='EXCHANGE'>
          <QueryClientProvider client={queryClientRef.current}>
            <ExchangeOptionView {...props} />
          </QueryClientProvider>
        </OptionProvider>
      </UBLProvider>
    </PdpConfigProvider>
  );
};

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${Gray_Light_White};
`;

const Loader = styled(MiniLoader)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const EditButton = styled(FaceButton)<{ borderColor: string; textColor: string }>`
  width: 100%;
  border-color: ${({ borderColor }) => borderColor};
  color: ${({ textColor }) => textColor};

  :disabled {
    background-color: ${vars.v3Color.gray150};
    color: ${vars.v3Color.gray400};
  }
`;

const ButtonWrapper = styled.div`
  padding: 0 16px 0 16px;
  position: absolute;
  width: 100%;
  bottom: 64px;
  left: 0;
  right: 0;
`;
